import { useTranslation } from "react-i18next";
import { SearchOutlined, SoundOutlined, StarOutlined } from "@ant-design/icons";
import { type BreadcrumbProps, Layout, Space } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Image } from "@av/components/atoms/Image";
import { Badge } from "@av/components/molecules/Badge";
import {
  HeaderBreadcrumb,
  type IgnoredBreadcrumbSegments,
} from "@av/components/organisms/nav/header-breadcrumb/HeaderBreadcrumb";
import { ProfileDropdown } from "@av/components/organisms/ProfileDropdown";
import { useNavSegments } from "./nav/hooks/useNavSegments";

import type { NavItem } from "./nav/types";
import type { AppSwitcherEntry, DisplayUser, HeaderAdminSettingsControls } from "./type";

type WebHeaderStyleProps = {
  isSubmenuOpen: boolean;
  subMenuExpandedWidth: number;
  isProductRoot: boolean;
  hideBottomBorder: boolean;
  zIndex: number;
};

const useStyles = createStyles(
  (
    { token, css },
    {
      isSubmenuOpen,
      subMenuExpandedWidth,
      isProductRoot,
      hideBottomBorder,
      zIndex,
    }: WebHeaderStyleProps
  ) => ({
    header: css`
      position: fixed;
      top: 0;
      z-index: ${zIndex};
      width: 100%;
      background: ${token.colorBgContainer};
      padding-top: ${token.paddingLG}px;
      padding-right: 63px;
      padding-bottom: ${token.paddingLG}px;
      padding-left: ${135 + (isSubmenuOpen ? subMenuExpandedWidth + 8 : 0)}px;
      height: 64px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: ${isProductRoot || hideBottomBorder
        ? "none"
        : `1px solid ${token.colorBorder}`};
      transition: padding ${token.motionDurationMid} ${token.motionEaseInOut};
    `,
    iconContainer: css`
      margin-left: ${60 + 12}px;
      margin-top: 10px;
      align-items: center;
    `,
    icon: css`
      font-size: 20px;
      color: ${token.colorTextSecondary};
    `,
    badgeWrapper: css`
      position: relative;
      display: inline-flex;
    `,
    profileWrapper: css`
      margin-bottom: ${token.marginXS}px;
    `,
  })
);

interface WebHeaderProps {
  hideBreadcrumb?: boolean; // Optional prop to control breadcrumb visibility
  secondaryBreadcrumbLabel?: React.ReactNode;
  apps: AppSwitcherEntry[];
  currentApp: string;
  onAppChange: (path: string) => void;
  isSubmenuOpen: boolean;
  navItems: NavItem[];
  subMenuExpandedWidth: number;
  onLogout: () => void;
  avaButton?: React.ReactNode;
  adminSettingsControls?: HeaderAdminSettingsControls;
  zIndex: WebHeaderStyleProps["zIndex"];
  user?: DisplayUser;
  isDemo: boolean;
  hideBottomBorder?: boolean;
  permissions: Array<string>;
  breadcrumbItems?: BreadcrumbProps["items"];
  ignoredSegmentsInBreadcrumb?: IgnoredBreadcrumbSegments;
}

function WebHeader(props: WebHeaderProps) {
  const {
    hideBreadcrumb,
    apps,
    currentApp,
    onAppChange,
    isSubmenuOpen,
    subMenuExpandedWidth,
    navItems,
    onLogout,
    avaButton,
    adminSettingsControls,
    zIndex,
    user,
    isDemo,
    secondaryBreadcrumbLabel,
    ignoredSegmentsInBreadcrumb,
    hideBottomBorder = false,
    permissions,
    breadcrumbItems,
  } = props;
  const { t } = useTranslation();
  const { Header } = Layout;

  const { secondarySegment } = useNavSegments(navItems);
  const { styles } = useStyles({
    isSubmenuOpen,
    subMenuExpandedWidth,
    isProductRoot: !secondarySegment,
    hideBottomBorder,
    zIndex: zIndex,
  });

  return (
    <Header className={styles.header}>
      <Space size="large">
        <HeaderBreadcrumb
          currentApp={currentApp}
          apps={apps}
          company={user?.company}
          onAppChange={onAppChange}
          hideBreadcrumb={hideBreadcrumb}
          navItems={navItems}
          secondaryBreadcrumbLabel={secondaryBreadcrumbLabel}
          ignoredSegmentsInBreadcrumb={ignoredSegmentsInBreadcrumb}
          items={breadcrumbItems}
        />
      </Space>
      <div className={styles.iconContainer}>
        <Space size="large" align="center">
          {avaButton}
          {isDemo && (
            <>
              <SearchOutlined className={styles.icon} data-testid="header-search-icon" />
              <span className={styles.badgeWrapper}>
                <SoundOutlined className={styles.icon} data-testid="header-notifications-bell" />
                <Badge testIdPrefix="header-notifications" dot offset={[-2, 2]} size="small" />
              </span>
              <StarOutlined className={styles.icon} data-testid="header-favorites-icon" />
            </>
          )}
          {adminSettingsControls?.isAdminSettingsVisible &&
            adminSettingsControls?.hasAdminSettingsPermission(permissions) && (
              <Button
                testIdPrefix="admin-settings-nav"
                type="text"
                icon={
                  <Image
                    testIdPrefix="admin-settings-nav-icon"
                    src="/admin-settings/admin_panel_settings.svg"
                    preview={false}
                  />
                }
                aria-label={t("platform.adminSettings.navButton")}
                onClick={adminSettingsControls.openAdminSettings}
              />
            )}
          <div className={styles.profileWrapper}>
            <ProfileDropdown isDemo={isDemo} onLogout={onLogout} user={user} />
          </div>
        </Space>
      </div>
    </Header>
  );
}

export { WebHeader };
